import { Distribution, DistributionParam } from "@custom-types/graphs"
import { AppNode, NodeType } from "@features/builder/graph/types"
import {
  getCategoricalCategoryKeys,
  getCategoryDisplayName,
  getCategoryDisplayNames,
  isUniqueNameForRename,
} from "@features/builder/NodeDrawer/NodeForm/helpers"
import {
  MAX_POSSIBLE_VALUES,
  UNBOUNDED_SUPPORT_FLOOR_OFFSET,
} from "@features/builder/helpers/consts"
import { getLagNodePossibleMinMaxLagValue } from "@features/builder/helpers/nodes"
import { DECIMAL_PLACES_OPTIONS } from "@features/builder/NodeDrawer/NodeForm/UnitFields/UnitField"
import { ValidationError, getLabelFormatSchema } from "@utils/form"
import { createLagNodeLabel, isNodeLabelUnique } from "@utils/node/labelValidation"
import { getParentsList, hasChildNodes } from "@utils/node/getParentsList"
import { distributionProperties } from "utils/graph"
import * as yup from "yup"
import { isNodeReservedKeyword } from "@utils/node/keywords"
import { isNumericString } from "@utils/string"

const VALID_CATEGORY_NAME_REGEX = /^[a-zA-Z0-9_]+$/

export const errMsg = {
  CATEGORY_NAME_MUST_BE_UNIQUE: "Category name must be unique",
  CATEGORY_NAME_NO_UNDERSCORE: 'Category name is restricted to alphanumeric and "_"',
  CATEGORY_NAME_NO_RESERVED_KEYWORD: "Category name is a reserved keyword",
  CATEGORY_NAME_CANT_DELETE_LAST: "You can not remove last category",
}

export const getUniqueNodeNameSchemaConfig = (
  node: AppNode,
  existingNodes: AppNode[],
): yup.TestConfig<string | undefined> => {
  const existingLabels = existingNodes
    .filter(({ id, type }) => id !== node.id && type !== NodeType.Group)
    .map(({ data }) => data.label)

  return {
    name: "is-node-name-unique",
    message: "This node name is already in use",
    test: (value, ctx) => {
      if (ctx.parent.type === NodeType.Group) {
        return true
      }

      const label =
        ctx.parent.type === NodeType.Lag ? createLagNodeLabel(value, ctx.parent.lagValue) : value

      return isNodeLabelUnique(label, existingLabels)
    },
  }
}

export const getLagValueRespectingExistingConnectionsSchemaConfig = (
  node: AppNode,
  existingNodes: AppNode[],
): yup.TestConfig<number | undefined> => {
  return {
    name: "is-lag-value-respecting-existing-connections",
    test: (value: number | undefined, ctx) => {
      if (ctx.parent.type === NodeType.Group) {
        return true
      }

      if (!value) return true
      if (node.type !== NodeType.Lag) return true

      const { min, max } = getLagNodePossibleMinMaxLagValue(node, existingNodes)

      const isBetweenMinAndMax = value >= min && value <= max
      if (isBetweenMinAndMax) return true

      return ctx.createError({
        message: `Lag value is incorrect due to existing connections of this node.
        The value has to be lower or equal to ${max} (min lag value of parent nodes) 
        AND higher or equal to ${min} (max lag value of children nodes).`,
      })
    },
  }
}

type DistributionRules = {
  [K in Distribution]?: {
    [P in DistributionParam]?: yup.AnySchema
  }
}

/**
 * `ubound` rule shared by the distributions with unbounded discrete support — Geometric,
 * Logseries and Poisson. They differ only in where their support starts relative to
 * `loc`, which `UNBOUNDED_SUPPORT_FLOOR_OFFSET` holds.
 *
 * All have infinite support, so `ubound` is what makes the state space enumerable.
 * A leaf is merely sampled and needs no upper cut; a node with children has its states
 * enumerated to build each child's rows.
 *
 * Two bounds on it: at least `max(loc + offset, lbound)`, or there is nothing to sample,
 * and at most `MAX_POSSIBLE_VALUES` values counted from the support floor, or a child's
 * row count explodes.
 */
const getUnboundedSupportUboundSchema = (hasChildren: boolean, distribution: Distribution) => {
  const base = hasChildren
    ? yup.string().nullable().required("Required once this node has a child")
    : yup.string().nullable()

  return base.test("ubound-range", function (value) {
    const { loc, lbound } = this.parent
    const offset = UNBOUNDED_SUPPORT_FLOOR_OFFSET[distribution]

    if (offset === undefined) return true
    if (!isNumericString(value) || !isNumericString(loc)) return true

    const firstState = Number(loc) + offset
    const lowestState = isNumericString(lbound) ? Math.max(firstState, Number(lbound)) : firstState

    if (lowestState > Number(value)) {
      return this.createError({ message: `Must be ${lowestState} or greater` })
    }

    const maxUbound = firstState + MAX_POSSIBLE_VALUES - 1
    if (Number(value) > maxUbound) {
      return this.createError({ message: `Must be ${maxUbound} or less` })
    }

    return true
  })
}

const getDiscreteDistributionRules = (hasChildren: boolean): DistributionRules => ({
  [Distribution.Binomial]: {
    [DistributionParam.n]: yup
      .string()
      .required("Required")
      .test("is-integer", "must be an integer", (value) => Number.isInteger(Number(value)))
      .test("is-non-negative", "must be equal or greater than 0", (value) => Number(value) >= 0),
    [DistributionParam.p]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Geometric]: {
    [DistributionParam.p]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.ubound]: getUnboundedSupportUboundSchema(
      hasChildren,
      Distribution.Geometric,
    ),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Hypergeometric]: {}, // NOT SUPPORTED
  [Distribution.Logseries]: {
    [DistributionParam.p]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.ubound]: getUnboundedSupportUboundSchema(
      hasChildren,
      Distribution.Logseries,
    ),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Poisson]: {
    [DistributionParam.mu]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.ubound]: getUnboundedSupportUboundSchema(hasChildren, Distribution.Poisson),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
})

const continuousDistributionRules: DistributionRules = {
  [Distribution.Weibull]: {
    [DistributionParam.c]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Wald]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Vonmises]: {
    [DistributionParam.kappa]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Uniform]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Triangular]: {
    [DistributionParam.c]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.T]: {
    [DistributionParam.df]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Rayleigh]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Power]: {
    [DistributionParam.a]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Pareto]: {
    [DistributionParam.b]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Normal]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.NonCentralF]: {
    [DistributionParam.dfn]: yup.string().required("Required"),
    [DistributionParam.dfd]: yup.string().required("Required"),
    [DistributionParam.nc]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.NonCentralChisquare]: {
    [DistributionParam.df]: yup.string().required("Required"),
    [DistributionParam.nc]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Lognormal]: {
    [DistributionParam.s]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Logistic]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Laplace]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Gumbel]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Gamma]: {
    [DistributionParam.a]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.F]: {
    [DistributionParam.dfn]: yup.string().required("Required"),
    [DistributionParam.dfd]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Exponential]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Chisquare]: {
    [DistributionParam.df]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Cauchy]: {
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
  [Distribution.Beta]: {
    [DistributionParam.a]: yup.string().required("Required"),
    [DistributionParam.b]: yup.string().required("Required"),
    [DistributionParam.loc]: yup.string().required("Required"),
    [DistributionParam.scale]: yup.string().required("Required"),
    [DistributionParam.ubound]: yup.string().nullable(),
    [DistributionParam.lbound]: yup.string().nullable(),
  },
}

const getDistributionRules = (hasChildren: boolean): DistributionRules => ({
  ...continuousDistributionRules,
  ...getDiscreteDistributionRules(hasChildren),
})
// distribution related params (loc, p, n, lbound, ubound etc.)
type DisitributionParameterSchema = { [P in DistributionParam]?: yup.StringSchema }

// distribution itself (Categorical, Normal, Binomial etc.)  }
type DistributionSchema = { distribution: yup.StringSchema<string, yup.AnyObject, undefined, ""> }

type ParentWeightingSchema = { [parentNodeId: string]: yup.StringSchema }

// user defined keys for categorical nodes
type CategorySchema = { [categoryName: string]: yup.StringSchema }

type PropertyRowValidationSchema = DisitributionParameterSchema &
  DistributionSchema &
  ParentWeightingSchema &
  CategorySchema

const getPropertiesRowSchema = (node: AppNode, hasChildren: boolean) => {
  const distributionRules = getDistributionRules(hasChildren)
  const parentIds = getParentsList(node)
  const dfCpd = "originalNode" in node.data ? node.data.originalNode.properties.df_cpd : null
  const parentIdsReferencedInDfCpd = dfCpd
    ? parentIds.filter((id) => Object.keys(dfCpd).includes(id))
    : []

  const rowSchema: PropertyRowValidationSchema = {
    distribution: yup.string().oneOf(Object.keys(distributionProperties)).required(),
  }

  // set parent weightings validation schema as required if they are used in df_cpd
  parentIdsReferencedInDfCpd.forEach((parentId) => {
    rowSchema[parentId] = yup.string().required(ValidationError.REQUIRED)
  })

  // set distribution parameters validation schema based on selected distribution
  Object.entries(distributionRules).forEach(([, rules]) => {
    Object.keys(rules).forEach((param) => {
      const distributionParam = param as keyof typeof rules
      // already defined
      if (rowSchema[distributionParam]) return

      rowSchema[distributionParam] = yup
        .string()
        .when("distribution", ([distributionValue], schema) => {
          const distributionSchema = distributionRules[distributionValue as Distribution]
          if (!distributionSchema) return schema

          const paramSchema = distributionSchema[distributionParam as DistributionParam]
          if (!paramSchema) return schema

          return paramSchema
        })
    })
  })

  return yup
    .object(rowSchema)
    .test("categorical-parameters-sum", "Paremeters combined must equal 1", (propertiesRow) => {
      if (node.type !== NodeType.Categorical) return true

      const paramsKeys = Object.keys(propertiesRow).filter(
        (key) => !parentIds.includes(key) && key !== "distribution",
      ) as (keyof typeof propertiesRow)[]

      const sum = paramsKeys.reduce((result, key) => {
        const value = Number(propertiesRow[key as DistributionParam])
        if (isNaN(value)) return result

        return Number(result) + value
      }, 0)

      return sum === 1
    })
}

const testUniqueArray = (
  errorMessage: string = "Array must have unique values.",
  listNonUniqueValues: boolean = true,
) => {
  return (value: (string | undefined)[] | undefined, ctx: yup.TestContext<yup.AnyObject>) => {
    if (!value || !value.length) return true

    const nonUniqueValues = value.filter((item, index) => value.indexOf(item) !== index)
    const isUnique = nonUniqueValues.length === 0

    return (
      isUnique ||
      ctx.createError({
        message: `${errorMessage} ${
          listNonUniqueValues ? ` Duplicated values: ${nonUniqueValues.join(", ")}` : ""
        }`,
      })
    )
  }
}

const testReservedKeyword = () => {
  return (value: string | undefined, ctx: yup.TestContext<yup.AnyObject>) => {
    const isReserved = isNodeReservedKeyword(value ?? "")
    if (isReserved) return ctx.createError({ message: `"${value ?? ""}" is a reserved keyword.` })

    return true
  }
}

const getCategoryDisplayNamesSchema = (node: AppNode) => {
  if (node.type !== NodeType.Categorical) {
    return yup.object().default({})
  }

  const categoryKeys = getCategoricalCategoryKeys(node.data.originalNode.properties)
  const categoryDisplayNames = getCategoryDisplayNames(node.data.originalNode.properties)

  return yup
    .object(
      Object.fromEntries(
        categoryKeys.map((key) => [key, yup.string().required(ValidationError.REQUIRED)]),
      ),
    )
    .test("categorical-display-names-valid", "Category name is invalid", (value, ctx) => {
      const displayNames = categoryKeys.map((key) => {
        const submittedDisplayName = value && key in value ? value[key] : undefined

        return submittedDisplayName ?? getCategoryDisplayName(categoryDisplayNames, key) ?? ""
      })

      for (const [index, key] of categoryKeys.entries()) {
        const displayName = displayNames[index] ?? ""

        if (!VALID_CATEGORY_NAME_REGEX.test(displayName)) {
          return ctx.createError({
            message: errMsg.CATEGORY_NAME_NO_UNDERSCORE,
          })
        }

        if (!isUniqueNameForRename(displayName, displayNames, index)) {
          return ctx.createError({ message: errMsg.CATEGORY_NAME_MUST_BE_UNIQUE })
        }

        if (isNodeReservedKeyword(displayName)) {
          return ctx.createError({ message: errMsg.CATEGORY_NAME_NO_RESERVED_KEYWORD })
        }

        if (!key) {
          return ctx.createError({ message: ValidationError.REQUIRED })
        }
      }

      return true
    })
}

export const getValidationSchema = (node: AppNode, existingNodes: AppNode[]) => {
  const hasChildren = hasChildNodes(node.id, existingNodes)

  const validTypes = Object.values(NodeType).filter((type) => {
    if (node.type === NodeType.Empty && type === NodeType.Empty) return false

    return true
  })

  return yup.object().shape({
    label: yup
      .string()
      .required(ValidationError.REQUIRED)
      .when("type", ([type], schema) => {
        if (type === NodeType.Group) {
          return schema
        }

        return getLabelFormatSchema(schema).test(getUniqueNodeNameSchemaConfig(node, existingNodes))
      }),
    lagValue: yup.number().when("type", ([type], schema) => {
      if (type === NodeType.Group) {
        return schema
      }

      return schema
        .typeError(ValidationError.NUMBER)
        .positive(ValidationError.NUMBER_POSITIVE)
        .integer(ValidationError.NUMBER_INTEGER)
        .required(ValidationError.REQUIRED)
        .test(getLagValueRespectingExistingConnectionsSchemaConfig(node, existingNodes))
    }),
    type: yup.string().required().oneOf(validTypes, "Node type must not be ${value}"),
    unit: yup.object({
      isEmpty: yup.boolean().required(),
      usePercentageValue: yup.boolean().required(),
      decimalPlaces: yup.string().oneOf(DECIMAL_PLACES_OPTIONS).required(),
      suffix: yup.string(),
    }),
    description: yup.string(),
    comments: yup.string(),
    link: yup
      .string()
      .test("link-parent-conflict", "Node cannot have both a link and parents", (value) => {
        if (!value) return true

        const parents = getParentsList(node)
        const hasParents = parents && parents.length > 0

        return !hasParents
      }),
    color: yup.string().nullable(),
    properties: yup.array().of(getPropertiesRowSchema(node, hasChildren)).required(),
    categoryDisplayNames: getCategoryDisplayNamesSchema(node),
    attributeProperties: yup.object({
      index: yup
        .array()
        .required(ValidationError.REQUIRED)
        .of(
          yup
            .string()
            .required(ValidationError.REQUIRED)
            .matches(/^[a-zA-Z0-9_]+$/, "Only letters, numbers and _")
            .test("is-reserved-keyword", "", testReservedKeyword()),
        )
        .test(
          "is-attribute-property-table-row-name-unique",
          "",
          testUniqueArray("Row name must be unique."),
        ),
      columns: yup
        .array()
        .required(ValidationError.REQUIRED)
        .of(
          yup
            .string()
            .required(ValidationError.REQUIRED)
            .matches(/^[a-zA-Z0-9_]+$/, "Only letters, numbers and _")
            .test("is-reserved-keyword", "", testReservedKeyword()),
        )
        .test(
          "is-attribute-property-table-column-name-unique",
          "",
          testUniqueArray("Column name must be unique."),
        ),
      data: yup
        .array()
        .required(ValidationError.REQUIRED)
        .of(
          yup
            .array()
            .required(ValidationError.REQUIRED)
            .of(yup.string().required(ValidationError.REQUIRED)),
        ),
    }),
    expression: yup.string(),
    sample_value: yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .nullable()
      .optional(),
    forecast_mnemonic: yup.string().nullable(),
    scenario_mnemonic: yup.string().nullable(),
    historic_mnemonic: yup.string().nullable(),
    tags: yup
      .array()
      .of(
        yup.object({
          tag: yup.string().required(ValidationError.REQUIRED),
          value: yup.string().required(ValidationError.REQUIRED),
        }),
      )
      .test("are-tags-unique", "", (tags, ctx) => {
        if (!tags) return true

        const tagNames = tags.map((tag) => tag.tag)
        const nonUniqueTagNames = tagNames.filter((tag, index) => tagNames.indexOf(tag) !== index)
        const nonUniqueTags = Array.from(new Set(nonUniqueTagNames))

        if (nonUniqueTags.length === 0) return true

        return ctx.createError({ message: `Duplicated tag names: ${nonUniqueTags.join(", ")}` })
      })
      .nullable(),
    graph_tags: yup
      .array()
      .of(
        yup.object({
          tag: yup.string().required(ValidationError.REQUIRED),
          values: yup
            .array()
            .of(yup.string().required())
            .min(1, ValidationError.REQUIRED)
            .required(ValidationError.REQUIRED),
        }),
      )
      .test("are-graph-tags-unique", "", (tags, ctx) => {
        if (!tags) return true

        const tagNames = tags.map((tag) => tag.tag)
        const nonUniqueTagNames = tagNames.filter((tag, index) => tagNames.indexOf(tag) !== index)
        const nonUniqueTags = Array.from(new Set(nonUniqueTagNames))

        if (nonUniqueTags.length === 0) return true

        return ctx.createError({ message: `Duplicated tag names: ${nonUniqueTags.join(", ")}` })
      })
      .nullable(),
    exportExcluded: yup.boolean().default(false).nullable(),
  })
}
